import { defineModal, Loader, Modal } from '@libs/ui/ds'
import { useTrackScheduleFeaturesInteraction } from '@spa/core/externals/segment'
import type { ScheduleFeatureShiftPlanificationType } from '@spa/core/externals/segment/methods/track/types'
import useMemoSelector from '@spa/redux/hooks/useMemoSelector'
import { realHoursModalDef } from '@spa/scenes/Plannings/Modals/RealHoursModal/RealHoursModal'
import type { FC } from 'react'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { fetchMembership } from 'snap-redux/modules/memberships'
import { getMembershipById } from 'snap-redux/modules/memberships/selectors'
import { getRestById } from 'snap-redux/modules/rests/selectors'
import { fetchShift } from 'snap-redux/modules/shifts'
import { getShiftById } from 'snap-redux/modules/shifts/selectors'
import { getCurrentUserRoleRights } from 'snap-redux/modules/users/selectors'
import { fetchWeeklyschedule } from 'snap-redux/modules/weeklyschedules'
import { getWeeklyScheduleById } from 'snap-redux/modules/weeklyschedules/selectors'

import { UnvalidateShiftFormLegacy } from '../../Forms'
import { getOwnMembershipValidation } from '../../redux/selectors'

type UnvalidateHoursModalProps = {
  shiftId: number
  restId: number
  membershipId: number
  weeklyscheduleId: number
  // For Month View V2, we compute the possibility to invalidate a shift directly via the permissions on a shift
  precomputedCanValidate?: boolean
  precomputedCanInvalidate?: boolean
  onSuccess?: () => void
}

/**
 * @deprecated use unvalidateHoursModalDef
 */
export const unvalidateHoursModalLegacyDef =
  defineModal<UnvalidateHoursModalProps>({
    name: 'unvalidateHoursModalLegacy'
  })

/**
 * @deprecated use UnvalidateHoursModal
 */
const UnvalidateHoursModalLegacy: FC = () => {
  const scheduleTracker = useTrackScheduleFeaturesInteraction()
  const { close, isOpen, data } = unvalidateHoursModalLegacyDef.useModal()
  const { open: openRealHours } = realHoursModalDef.useDispatchModal()
  const dispatch = useDispatch()

  const {
    shiftId,
    restId,
    membershipId,
    weeklyscheduleId,
    precomputedCanValidate,
    precomputedCanInvalidate,
    onSuccess
  } = data as UnvalidateHoursModalProps
  const roles = useMemoSelector(getCurrentUserRoleRights)
  const shift = useMemoSelector(state => getShiftById(state, shiftId))
  const rest = useMemoSelector(state =>
    restId ? getRestById(state, shiftId) : null
  )
  const membership = useMemoSelector(state =>
    getMembershipById(state, membershipId)
  )
  const weeklyschedule = useMemoSelector(state =>
    getWeeklyScheduleById(state, weeklyscheduleId)
  )

  const canInvalidateOwnShift = useMemoSelector(state =>
    getOwnMembershipValidation(state, {
      membershipId,
      policyKey: 'canInvalidateOwnRealHours'
    })
  )

  const computeInvalidationPermission = () => {
    // Month View V2
    if (precomputedCanInvalidate !== undefined) {
      return precomputedCanInvalidate
    }
    // Other views
    return (
      weeklyschedule.permissions?.toJS().can_validate && canInvalidateOwnShift
    )
  }

  const canInvalidateShift = computeInvalidationPermission()

  useEffect(() => {
    if (!weeklyschedule) {
      dispatch(fetchWeeklyschedule(weeklyscheduleId))
    }
    if (!membership) {
      dispatch(fetchMembership(membershipId))
    }
    /**
     * Force to fetch shift in order to get shift_event data
     */
    dispatch(fetchShift(shiftId))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isLoading =
    !shift || !membership || !weeklyschedule || (restId && !!rest)

  if (isLoading) {
    return <Loader.Overlay />
  }

  return (
    <Modal opened={isOpen} onClose={close} variant='right'>
      <UnvalidateShiftFormLegacy
        shift={shift}
        isManager={roles.atLeast.manager}
        canInvalidate={canInvalidateShift}
        formSuccess={() => {
          close()
          onSuccess?.()
          scheduleTracker.scheduleElementCardOpened(
            'shift',
            shift.planification_type as ScheduleFeatureShiftPlanificationType,
            {
              published: weeklyschedule.published,
              validated: Boolean(shift.validated_at),
              declared: Boolean(shift.real_hours_declared_correct_at)
            }
          )
          openRealHours({
            shiftId,
            weeklyscheduleId,
            membershipId,
            precomputedCanValidate,
            onSuccess
          })
        }}
      />
    </Modal>
  )
}

/**
 * @deprecated use unvalidateHoursModalDef
 */
export default unvalidateHoursModalLegacyDef.withModalShow(
  UnvalidateHoursModalLegacy
)
